import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';

import { tokens, Text } from '@hotjar/hotsauce';
import { RemovalConfirmation } from '@hotjar/comments/components/RemovalConfirmation';
import { Thread } from '@hotjar/comments/components/Thread';
import { useComments } from '@hotjar/comments/hooks/useComments';
import type { UseCommentsProps } from '@hotjar/comments/hooks/useComments';
import { noop } from '@hotjar/webapp/lib/function';
import type { HighlightsListItem } from '@hotjar/webapp/domains/highlights/model';
import { invalidateQuery } from '@hotjar/webapp/features/Highlights/util';

import { SkeletonLoader } from './SkeletonLoader';
import { AutoCreatedComment } from '../AutoCreatedComment';

const HeadingSkeleton = styled(SkeletonLoader)`
  width: 110px;
  height: 20px;
  margin-top: ${tokens.spacer.x1};
  margin-bottom: ${tokens.spacer.x0pt5};
`;

const CommentInputSkeleton = styled(SkeletonLoader)`
  width: 260px;
  height: 36px;
`;

const ReplyButtonSkeleton = styled(SkeletonLoader)`
  width: 67px;
  height: 32px;
  margin: ${tokens.spacer.x1} 0 0 auto;
`;

const Wrapper = styled.section`
  align-content: start;
  background-color: inherit;
  display: grid;
  flex-grow: 1;
  padding: 0 ${tokens.spacer.x2};
`;

const Heading = styled(Text)`
  margin-bottom: ${tokens.spacer.x0};
  margin-top: ${tokens.spacer.x1};
`;

const CommentsWrapper = styled.section`
  max-width: 260px;
`;

export interface CommentsThreadProps {
  isLoading?: boolean;
  className?: string;
  commentId?: string | null;
  currentUser: UseCommentsProps['currentUser'];
  highlight: Pick<HighlightsListItem, 'id' | 'createdDate' | 'autoCreated'>;
  organizationId: string;
  siteId: string;
}

export function CommentsThread({
  isLoading = false,
  className,
  commentId,
  currentUser,
  highlight,
  organizationId,
  siteId,
}: CommentsThreadProps): JSX.Element | null {
  const { t } = useTranslation();

  const { areThreadsFetched, threads, threadProps, removalConfirmationProps, renderNewThread } =
    useComments({
      siteId,
      organizationId: Number(organizationId),
      widgetId: Number(highlight.id),
      widgetType: 'highlight',
      scopeId: commentId ? Number(commentId) : null,
      currentUser,
      apiToken: null,
      onThreadAdded: noop,
      onThreadRemoved: noop,
      onReplyAdded: noop,
      newThreadMetadata: {},
      compactNewThread: true,
      autoFocusTextField: false,
      setActiveItem: () => null,
      invalidateQuery,
    });

  if (isLoading || !areThreadsFetched) {
    return (
      <Wrapper className={className}>
        <HeadingSkeleton />
        <CommentInputSkeleton />
        <ReplyButtonSkeleton />
      </Wrapper>
    );
  }

  return (
    <Wrapper className={className} data-testid="highlight-comments">
      <Heading as="p" textType="subtitle2">
        {t('highlights:single.comments')}
      </Heading>

      {highlight.autoCreated && threads.length === 0 && (
        <AutoCreatedComment highlightCreatedAt={highlight.createdDate} />
      )}

      <CommentsWrapper>
        {threads.length === 0 ? renderNewThread() : <Thread {...threadProps} thread={threads[0]} />}
      </CommentsWrapper>
      <RemovalConfirmation {...removalConfirmationProps} />
    </Wrapper>
  );
}
